import type {
    MegaGridColumn,
    MegaGridColumnFilter,
    MegaGridDisplayRow,
    MegaGridFacet,
    MegaGridFilterInstance,
    MegaGridFilterType,
    MegaGridGroupBy,
    MegaGridScalar,
    MegaGridSort,
} from './MegaGridTypes';

/** Strings with at most this many distinct values get an enum (pick-list) filter. */
const ENUM_MAX_DISTINCT = 20;

/**
 * Returns the column's explicit `filter`, or derives one from its `sortValue`
 * (numbers → number, dates → date, booleans → boolean, low-cardinality strings
 * → enum, everything else → text). Columns with `noFilter` or no `sortValue`
 * are not filterable.
 */
export function inferColumnFilter<T>(column: MegaGridColumn<T>, rows: T[]): MegaGridColumnFilter<T> | undefined {
    if (column.filter) return column.filter;
    if (column.noFilter || !column.sortValue) return undefined;
    const sortValue = column.sortValue;

    let sawNumber = false;
    let sawDate = false;
    let sawBool = false;
    const distinct = new Set<string>();
    for (const row of rows) {
        const v = sortValue(row);
        if (v === null || v === undefined || v === '') continue;
        if (v instanceof Date) sawDate = true;
        else if (typeof v === 'number') sawNumber = true;
        else if (typeof v === 'boolean') sawBool = true;
        else if (distinct.size <= ENUM_MAX_DISTINCT + 1) distinct.add(String(v));
    }

    let type: MegaGridFilterType;
    if (sawDate && !sawNumber) type = 'date';
    else if (sawNumber && distinct.size === 0 && !sawBool) type = 'number';
    else if (sawBool && distinct.size === 0 && !sawNumber) type = 'boolean';
    else if (distinct.size > 0 && distinct.size <= ENUM_MAX_DISTINCT) type = 'enum';
    else type = 'text';

    return { type, accessor: (row) => sortValue(row) as MegaGridScalar };
}

function toComparable(value: MegaGridScalar): string | number | null {
    if (value === null || value === undefined) return null;
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'boolean') return value ? 1 : 0;
    return value;
}

function compareValues(a: string | number | null, b: string | number | null): number {
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b));
}

const DAY_MS = 86_400_000;

function passesFilterInstance<T>(row: T, column: MegaGridColumn<T>, f: MegaGridFilterInstance): boolean {
    const filter = column.filter;
    if (!filter) return true;
    const raw = filter.accessor(row);
    const isBlank = raw === null || raw === undefined || raw === '';

    switch (f.op) {
        case 'blank':
            return isBlank;
        case 'notBlank':
            return !isBlank;
        case 'isTrue':
            return Boolean(raw) === true;
        case 'isFalse':
            return Boolean(raw) === false;
        case 'anyOf':
            return !f.values || f.values.length === 0 ? true : f.values.includes(String(raw ?? ''));
        case 'noneOf':
            return !f.values || f.values.length === 0 ? true : !f.values.includes(String(raw ?? ''));
        case 'contains':
        case 'notContains':
        case 'equals':
        case 'notEquals':
        case 'startsWith':
        case 'endsWith': {
            const needle = String(f.value ?? '').toLowerCase();
            if (!needle) return true;
            const hay = String(raw ?? '').toLowerCase();
            if (f.op === 'contains') return hay.includes(needle);
            if (f.op === 'notContains') return !hay.includes(needle);
            if (f.op === 'equals') return hay === needle;
            if (f.op === 'notEquals') return hay !== needle;
            if (f.op === 'startsWith') return hay.startsWith(needle);
            return hay.endsWith(needle);
        }
        case 'eq':
        case 'gt':
        case 'gte':
        case 'lt':
        case 'lte':
        case 'between': {
            const num = typeof raw === 'number' ? raw : Number(raw);
            if (Number.isNaN(num)) return false;
            const scale = filter.type === 'percent' ? 100 : 1;
            const a = f.value == null || f.value === '' ? null : Number(f.value) / scale;
            const b = f.value2 == null || f.value2 === '' ? null : Number(f.value2) / scale;
            if (f.op === 'eq') return a == null || num === a;
            if (f.op === 'gt') return a == null || num > a;
            if (f.op === 'gte') return a == null || num >= a;
            if (f.op === 'lt') return a == null || num < a;
            if (f.op === 'lte') return a == null || num <= a;
            return (a == null || num >= a) && (b == null || num <= b);
        }
        case 'dateOn':
        case 'before':
        case 'after':
        case 'dateBetween': {
            const time = raw instanceof Date ? raw.getTime() : raw ? new Date(String(raw)).getTime() : NaN;
            if (Number.isNaN(time)) return false;
            const a = f.value ? new Date(String(f.value)).getTime() : null;
            const b = f.value2 ? new Date(String(f.value2)).getTime() : null;
            if (f.op === 'dateOn') return a == null || (time >= a && time < a + DAY_MS);
            if (f.op === 'before') return a == null || time < a;
            if (f.op === 'after') return a == null || time >= a + DAY_MS;
            return (a == null || time >= a) && (b == null || time < b + DAY_MS);
        }
        default:
            return true;
    }
}

export function filterRows<T>(
    rows: T[],
    columns: MegaGridColumn<T>[],
    options: {
        search: string;
        searchAccessor?: (row: T) => string;
        filters: MegaGridFilterInstance[];
        facets: MegaGridFacet<T>[];
        facetValues: Record<string, string>;
    },
): T[] {
    const query = options.search.trim().toLowerCase();
    const colByKey = new Map(columns.map((c) => [c.key, c]));
    const active = options.filters
        .map((f) => ({ f, col: colByKey.get(f.columnKey) }))
        .filter((x): x is { f: MegaGridFilterInstance; col: MegaGridColumn<T> } => Boolean(x.col?.filter));
    const activeFacets = options.facets.filter((f) => options.facetValues[f.key] && options.facetValues[f.key] !== 'all');

    if (!query && active.length === 0 && activeFacets.length === 0) return rows;

    return rows.filter((row) => {
        if (query && options.searchAccessor && !options.searchAccessor(row).toLowerCase().includes(query)) return false;
        if (!active.every(({ f, col }) => passesFilterInstance(row, col, f))) return false;
        return activeFacets.every((f) => (f.accessor(row) ?? '') === options.facetValues[f.key]);
    });
}

export function sortRows<T>(rows: T[], columns: MegaGridColumn<T>[], sorts: MegaGridSort[]): T[] {
    if (sorts.length === 0) return rows;
    const resolved = sorts
        .map((s) => ({ sort: s, column: columns.find((c) => c.key === s.key) }))
        .filter((r): r is { sort: MegaGridSort; column: MegaGridColumn<T> } => Boolean(r.column?.sortValue));
    if (resolved.length === 0) return rows;

    return [...rows].sort((a, b) => {
        for (const { sort, column } of resolved) {
            const cmp = compareValues(toComparable(column.sortValue!(a)), toComparable(column.sortValue!(b)));
            if (cmp !== 0) return sort.dir === 'asc' ? cmp : -cmp;
        }
        return 0;
    });
}

/**
 * Flattens (optionally grouped) rows into a display list. Group headers are
 * emitted for each `groupBy` level in order; collapsed groups hide their rows.
 * Group ids are the joined path so nested collapse state stays stable.
 */
const BLANK_KEY = '—';

export function buildDisplayRows<T>(
    rows: T[],
    columns: MegaGridColumn<T>[],
    groupBy: MegaGridGroupBy[],
    expanded: Set<string>,
): MegaGridDisplayRow<T>[] {
    if (groupBy.length === 0) {
        return rows.map((row, index) => ({ kind: 'row', row, index }));
    }

    const out: MegaGridDisplayRow<T>[] = [];

    const walk = (subset: { row: T; index: number }[], level: number, parentId: string): void => {
        const group = groupBy[level];
        if (!group) return;
        const column = columns.find((c) => c.key === group.columnKey);
        const valueOf =
            column?.groupValue ??
            ((row: T): string => {
                const v = column?.sortValue?.(row);
                if (v === null || v === undefined || v === '') return BLANK_KEY;
                if (v instanceof Date) return v.toLocaleDateString();
                return String(v);
            });

        const buckets = new Map<string, { row: T; index: number }[]>();
        for (const item of subset) {
            const key = valueOf(item.row) || BLANK_KEY;
            const bucket = buckets.get(key);
            if (bucket) bucket.push(item);
            else buckets.set(key, [item]);
        }

        const dir = group.sortDir === 'asc' ? 1 : -1;
        const ordered = [...buckets.entries()].sort((a, b): number => {
            const aBlank = a[0] === BLANK_KEY;
            const bBlank = b[0] === BLANK_KEY;
            if (aBlank !== bBlank) return aBlank ? 1 : -1;
            if (group.sortMode === 'count') {
                const delta = a[1].length - b[1].length;
                if (delta !== 0) return delta * dir;
                return a[0].localeCompare(b[0]);
            }
            return a[0].localeCompare(b[0]) * dir;
        });

        for (const [key, bucket] of ordered) {
            const groupId = `${parentId}/${level}:${key}`;
            const label = column?.groupLabel ? String(column.groupLabel(key)) : key;
            out.push({ kind: 'group', id: groupId, label, depth: level, count: bucket.length, rows: bucket.map((item) => item.row) });
            if (!expanded.has(groupId)) continue;
            if (level + 1 < groupBy.length) walk(bucket, level + 1, groupId);
            else for (const item of bucket) out.push({ kind: 'row', row: item.row, index: item.index });
        }
    };

    walk(rows.map((row, index) => ({ row, index })), 0, 'g');
    return out;
}

/** Distinct enum options derived from the data when a filter omits explicit options. */
export function deriveEnumOptions<T>(rows: T[], column: MegaGridColumn<T>): { label: string; value: string }[] {
    if (!column.filter) return [];
    if (column.filter.options) return column.filter.options;
    const values = new Set<string>();
    for (const row of rows) {
        const raw = column.filter.accessor(row);
        if (raw !== null && raw !== undefined && raw !== '') values.add(String(raw));
    }
    return [...values].sort((a, b) => a.localeCompare(b)).map((v) => ({ label: v, value: v }));
}
